import React, { useState } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { slovakiaReviews, Review } from '../constants/reviews';

const Section = styled.section`
    padding: 60px 0;
    background: linear-gradient(135deg, #f8fafc 0%, #ffffff 50%, #f0f4f8 100%);
    position: relative;
`;

const Container = styled.div`
    max-width: 1200px;
    margin: 0 auto;
    padding: 0 2rem;
    position: relative;
    z-index: 2;

    @media (max-width: 768px) {
        padding: 0 1rem;
    }
`;

const SectionHeader = styled.div`
    text-align: center;
    margin-bottom: 3rem;
`;

const SectionTitle = styled(motion.h2)`
    font-size: 2.8rem;
    font-weight: 800;
    margin-bottom: 1rem;
    background: linear-gradient(135deg, #1a1a1a 0%, #667eea 50%, #764ba2 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    line-height: 1.1;

    @media (max-width: 768px) {
        font-size: 2rem;
    }
`;

const SectionSubtitle = styled(motion.p)`
    font-size: 1.1rem;
    color: #4a5568;
    line-height: 1.6;
    max-width: 500px;
    margin: 0 auto;
`;

const ReviewsGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
    gap: 1.5rem;
    margin-bottom: 2rem;

    @media (max-width: 768px) {
        grid-template-columns: 1fr;
        gap: 1rem;
    }
`;

const ReviewCard = styled(motion.div)`
    background: linear-gradient(135deg,
    rgba(255, 255, 255, 0.95) 0%,
    rgba(248, 250, 252, 0.95) 100%);
    backdrop-filter: blur(15px);
    border: 1px solid rgba(255, 255, 255, 0.8);
    border-radius: 16px;
    padding: 1.5rem;
    box-shadow:
            0 15px 30px rgba(0, 0, 0, 0.06),
            0 0 0 1px rgba(255, 255, 255, 0.6) inset;
    position: relative;
    overflow: hidden;

    &::before {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        height: 3px;
        background: linear-gradient(135deg, #667eea, #764ba2);
        border-radius: 16px 16px 0 0;
    }
`;

const ReviewHeader = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    margin-bottom: 1rem;
`;

const ReviewerInfo = styled.div`
    flex: 1;
`;

const ReviewerName = styled.h4`
    font-size: 1.1rem;
    font-weight: 700;
    color: #1a1a1a;
    margin-bottom: 0.2rem;
`;

const ReviewerLocation = styled.p`
    color: #667eea;
    font-weight: 600;
    font-size: 0.85rem;
    margin-bottom: 0.4rem;
`;

const ReviewDate = styled.p`
    color: #718096;
    font-size: 0.8rem;
`;

const RatingContainer = styled.div`
    display: flex;
    align-items: center;
    gap: 0.4rem;
`;

const Stars = styled.div`
    display: flex;
    gap: 0.1rem;
`;

const RatingText = styled.span`
    color: #4a5568;
    font-weight: 600;
    font-size: 0.85rem;
`;

const ReviewContent = styled.p`
    color: #4a5568;
    line-height: 1.6;
    font-size: 0.95rem;
    margin-bottom: 1rem;
`;

const ReadMoreButton = styled.button`
    background: none;
    border: none;
    color: #667eea;
    font-weight: 600;
    cursor: pointer;
    padding: 0;
    font-size: 0.9rem;
    transition: color 0.2s ease;

    &:hover {
        color: #764ba2;
    }
`;

const ReviewImages = styled.div`
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 0.4rem;
    margin-top: 1rem;
`;

const ReviewImage = styled(motion.img)`
    width: 100%;
    height: 60px;
    object-fit: cover;
    border-radius: 6px;
    cursor: pointer;
    transition: transform 0.3s ease;

    &:hover {
        transform: scale(1.05);
    }
`;

const StatsContainer = styled(motion.div)`
    background: linear-gradient(135deg,
    rgba(102, 126, 234, 0.05) 0%,
    rgba(118, 75, 162, 0.05) 100%);
    border-radius: 16px;
    padding: 2rem;
    text-align: center;
    border: 1px solid rgba(102, 126, 234, 0.1);
    margin-top: 2rem;
`;

const StatsGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 1.5rem;

    @media (max-width: 768px) {
        grid-template-columns: repeat(2, 1fr);
        gap: 1rem;
    }

    @media (max-width: 480px) {
        grid-template-columns: 1fr;
    }
`;

const StatItem = styled.div`
    text-align: center;
`;

const StatNumber = styled.div`
    font-size: 2rem;
    font-weight: 800;
    background: linear-gradient(135deg, #667eea, #764ba2);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    margin-bottom: 0.4rem;
`;

const StatLabel = styled.div`
    color: #4a5568;
    font-weight: 600;
    font-size: 0.85rem;
`;

const FloatingElements = styled.div`
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    pointer-events: none;
    z-index: 1;
`;

const FloatingElement = styled(motion.div)`
    position: absolute;
    background: linear-gradient(135deg, #667eea, #764ba2);
    border-radius: 12px;
    opacity: 0.1;

    &.element-1 {
        width: 80px;
        height: 80px;
        top: 10%;
        right: 5%;
    }

    &.element-2 {
        width: 50px;
        height: 50px;
        bottom: 20%;
        left: 3%;
    }
`;

const ShowMoreButton = styled(motion.button)`
    display: block;
    margin: 1.5rem auto;
    padding: 0.6rem 1.5rem;
    background: linear-gradient(135deg, #667eea, #764ba2);
    color: white;
    border: none;
    border-radius: 20px;
    font-size: 0.95rem;
    font-weight: 600;
    cursor: pointer;
    transition: transform 0.2s ease;

    &:hover {
        transform: scale(1.05);
    }
`;

const Testimonials: React.FC = () => {
    const [expandedReviews, setExpandedReviews] = useState<Set<number>>(new Set());
    const [showAllReviews, setShowAllReviews] = useState(false);

    const toggleReviewExpansion = (reviewId: number) => {
        const newExpanded = new Set(expandedReviews);
        if (newExpanded.has(reviewId)) {
            newExpanded.delete(reviewId);
        } else {
            newExpanded.add(reviewId);
        }
        setExpandedReviews(newExpanded);
    };
    const reviews: Review[] = slovakiaReviews;

    const displayedReviews = showAllReviews ? reviews : reviews.slice(0, 3);

    return (
        <Section id="testimonials">
            <FloatingElements>
                <FloatingElement
                    className="element-1"
                    animate={{
                        y: [0, -30, 0],
                        rotate: [0, 180, 360]
                    }}
                    transition={{
                        duration: 8,
                        repeat: Infinity,
                        ease: "easeInOut"
                    }}
                />
                <FloatingElement
                    className="element-2"
                    animate={{
                        y: [0, 20, 0],
                        rotate: [0, -90, 0]
                    }}
                    transition={{
                        duration: 6,
                        repeat: Infinity,
                        ease: "easeInOut",
                        delay: 1
                    }}
                />
            </FloatingElements>

            <Container>
                <SectionHeader>
                    <SectionTitle
                        initial={{ opacity: 0, y: 50 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8 }}
                        viewport={{ once: true }}
                    >
                        What Our Travelers Say
                    </SectionTitle>
                    <SectionSubtitle
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, delay: 0.2 }}
                        viewport={{ once: true }}
                    >
                        Don't just take our word for it - hear from travelers who've experienced our Slovakia castles tour
                    </SectionSubtitle>
                </SectionHeader>

                <ReviewsGrid>
                    {displayedReviews.map((review, index) => {
                        const isExpanded = expandedReviews.has(review.id);
                        const displayContent = isExpanded && review.fullContent
                            ? review.fullContent
                            : review.content;

                        return (
                            <ReviewCard
                                key={review.id}
                                initial={{ opacity: 0, y: 50 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.6, delay: index * 0.1 }}
                                viewport={{ once: true }}
                                whileHover={{ y: -5 }}
                            >
                                <ReviewHeader>
                                    <ReviewerInfo>
                                        <ReviewerName>{review.name}</ReviewerName>
                                        <ReviewerLocation>{review.location}</ReviewerLocation>
                                        <ReviewDate>{review.date} {review.verified && '• Verified Booking'}</ReviewDate>
                                    </ReviewerInfo>
                                    <RatingContainer>
                                        <Stars>
                                            {'★'.repeat(review.rating)}
                                        </Stars>
                                        <RatingText>{review.rating}/5</RatingText>
                                    </RatingContainer>
                                </ReviewHeader>

                                <ReviewContent>
                                    {displayContent}
                                    {review.fullContent && review.fullContent.length > review.content.length && (
                                        <ReadMoreButton
                                            onClick={() => toggleReviewExpansion(review.id)}
                                        >
                                            {isExpanded ? 'Show less' : 'Read more'}
                                        </ReadMoreButton>
                                    )}
                                </ReviewContent>

                                {review.images && (
                                    <ReviewImages>
                                        {review.images.map((image, imgIndex) => (
                                            <ReviewImage
                                                key={imgIndex}
                                                src={image}
                                                alt={`${review.name}'s tour photo ${imgIndex + 1}`}
                                                whileHover={{ scale: 1.05 }}
                                                whileTap={{ scale: 0.95 }}
                                                onError={(e) => {
                                                    e.currentTarget.style.display = 'none';
                                                }}
                                            />
                                        ))}
                                    </ReviewImages>
                                )}
                            </ReviewCard>
                        );
                    })}
                </ReviewsGrid>

                {reviews.length > 3 && (
                    <ShowMoreButton
                        onClick={() => setShowAllReviews(!showAllReviews)}
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                    >
                        {showAllReviews ? 'Show Less Reviews' : 'Show All Reviews'}
                    </ShowMoreButton>
                )}

                <StatsContainer
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8 }}
                    viewport={{ once: true }}
                >
                    <StatsGrid>
                        <StatItem>
                            <StatNumber>5.0</StatNumber>
                            <StatLabel>Average Rating</StatLabel>
                        </StatItem>
                        <StatItem>
                            <StatNumber>50+</StatNumber>
                            <StatLabel>Happy Travelers</StatLabel>
                        </StatItem>
                        <StatItem>
                            <StatNumber>100%</StatNumber>
                            <StatLabel>Would Recommend</StatLabel>
                        </StatItem>
                        <StatItem>
                            <StatNumber>3+</StatNumber>
                            <StatLabel>5-Star Reviews</StatLabel>
                        </StatItem>
                    </StatsGrid>
                </StatsContainer>
            </Container>
        </Section>
    );
};

export default Testimonials;